import { Button, Spinner, useLoadingTimeout } from '@citizen-walk/shared-ui';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnnouncementBanner, AnnouncementHistoryModal, AnnouncementModal } from '../announcements';
import {
  AccountRoleBadge,
  DeviceTransferModal,
  RecoveryRecoverModal,
  SettingsModal,
  useAnonymousAuth,
  useMyIdentity,
  useResolvedNickname,
} from '../auth';
import {
  DistrictPendingScreen,
  useDistrict,
  useDistrictState,
  useReportAccessDeniedOnError,
} from '../district';
import {
  FilterButton,
  FilterPanel,
  OnlyMineToggle,
  PhotoDownloadModal,
  PostDetailModal,
  applyPostFilters,
  removeOptimisticPost,
  useAiCategoryMasters,
  useMainFilters,
  useOptimisticPosts,
  useOutbox,
  usePosts,
  type PostMinimal,
} from '../posts';
import { useApplyUrlSessionId, useMainSession } from '../sessions';
import {
  BottomTabBar,
  Drawer,
  DrawerToggleButton,
  MultiTabBanner,
  useActionOutbox,
  useOnlineStatus,
} from '../shell';
import { SurveyListModal } from '../survey';
import { VerificationListPage } from '../verification';
import { MapLegend } from './MapLegend';
import { MapView } from './MapView';
import type { GisFeatureClickInfo } from './MapView';
import { GisFeaturePanel } from './GisFeaturePanel';
import { GisSettingsModal } from './GisSettingsModal';
import { useDistrictGisLayers } from './useDistrictGisLayers';
import { useColorRule } from './useColorRule';
import { useDistrictPmtiles } from './useDistrictPmtiles';
import { zoomForRadius } from './mapDefaults';

/**
 * 地図タブ page。`/m/:urlToken/map` で render され、AppHome (post / list) と同じ shell を共有。
 *
 * Sprint AD — 旧 `old/apps/user/src/pages/tabs/MapTab.tsx` (L435-471 absolute inset-0 全画面構造) と
 * 同じ「地図がメインコンテンツに full-bleed で描画される」体験へ揃える。AppHome と同等の shell
 * (compact header + Drawer + drawer-triggered modals + BottomTabBar) を本 page にも複製し、
 * 旧 MainPage L132-178 と同じ全画面 `h-dvh` 構成の中で MapView が flex-1 main を埋める。
 *
 * 旧 MapTab には存在しない要素を撤去:
 *   - 「📍 地図表示」h1 + 「← 戻る」link
 *   - 「地区: name (id) / 投稿: N 件、位置情報あり N 件」debug 行
 *   - 「※ Phase 5.3 minimum:」 dev 注記
 *   - 「🗺 オフライン地図: 利用可能 / 未生成」inline status text
 *   - inline offline 注意 banner (AppHome と同じ header badge に統合)
 *   - inline PostFilterBar (旧 FilterButton overlay → FilterPanel side drawer pattern に倣い、
 *     floating 🔍 button → 折畳 panel の overlay 形式に変更)
 *
 * 維持 (旧 MapTab 同等):
 *   - 地図が画面いっぱい (header と BottomTabBar の間を埋める)
 *   - PMTiles 描画 (Sprint W で結線、`tileSource==='pmtiles'` 時に MapView 内 overlay 表示)
 *   - marker click → PostDetailModal (Sprint X2 で再利用)
 *   - useCurrentSession + useApplyUrlSessionId (`/m/:urlToken/:sessionId/map` deep route)
 *
 * 既知の落とし穴対応 (L2-L4 移植時 docs/97 §5):
 *   - PMTiles 連携 + クラスタリング + 長押し「ここで投稿」マーカー (旧 createPendingLocationMarker
 *     L475-549) は R-33 後続 (docs/98)、本 commit は UI 整合性のみ。
 */
export function MapPage() {
  const auth = useAnonymousAuth();
  const uid = auth.status === 'authenticated' ? auth.user.uid : null;
  const district = useDistrict();
  const districtId = district?.id ?? null;
  // 地区解決状態 (loading/error/ready)。未解決時は末尾 gate で DistrictPendingScreen を表示。
  const districtState = useDistrictState();
  // Sprint AM: 30s timeout 後の retry 用 key (UI2 無限スピナー解消)
  const [postsRetryKey, setPostsRetryKey] = useState(0);
  // 地区フォールバック根絶: 地区未解決のあいだ districtId は null。各 hook は no-op する。
  // R-70: 「自分」= 現 uid + 端末引き継ぎ元。docs/28: ぼかし前の写真を本人にだけ見せる判定にも使う。
  const identity = useMyIdentity(uid, districtId);
  const postsState = usePosts(uid, districtId, postsRetryKey, identity.allUids);
  // Sprint AM: 30s 経過判定 (overlay indicator 用)
  const postsLoadTimedOut = useLoadingTimeout(postsState.status === 'loading');
  // 地区「強い停止」Phase 1: キャッシュ済み一般ユーザーが停止後に投稿を読めず permission-denied に
  // なったら DistrictProvider に通知 → DistrictGate が「現在非公開中」画面へ切替える。
  useReportAccessDeniedOnError(postsState.status === 'error' ? postsState.error : undefined);
  // 手書きメモの作者名解決 (docs/24 / docs/111 E-2): localStorage (実 districtId) が空でも
  // membership doc の正規ニックネームにフォールバックする共通 hook。投稿 authorNickname と同一ロジック。
  const memoNickname = useResolvedNickname(uid, districtId) || null;
  // R-70 方針B: 端末引き継ぎ後も「自分の投稿」(現 uid + 引き継ぎ元 linkedUids) を正しく扱う。
  // 案1 統合: 「現セッション」を membership.mainSessionId に一本化 (AppHome と同一)。
  const mainSession = useMainSession(uid, districtId);
  const { state: currentSessionState, setCurrentSessionId } = mainSession;
  const currentSessionId = currentSessionState.currentSessionId;
  useApplyUrlSessionId(currentSessionState, currentSessionId, setCurrentSessionId);
  // Phase F (old2 FilterContext 逐語移植): 集約 filter state (AppHome と共通の useMainFilters)。
  const filters = useMainFilters();
  const categoryMasters = useAiCategoryMasters(district?.aiAnalysisSettingsId);
  const sessionOptions = useMemo(
    () => currentSessionState.items.map((s) => ({ id: s.id, name: s.name })),
    [currentSessionState.items],
  );
  const [detailPost, setDetailPost] = useState<PostMinimal | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  // Feature: 自分の投稿写真まとめてダウンロード modal (Drawer から開く、全タブで利用可能にするため)。
  const [photoDownloadOpen, setPhotoDownloadOpen] = useState(false);
  // 「ここで投稿」pending マーカーは MapView 内 (maplibregl.Marker、地点に追従) に移管 (旧 MapTab L381-412)。
  // MapPage は確定時の遷移のみ担当 (onPostHere)。
  const navigate = useNavigate();

  // Drawer + drawer-triggered modal 群 (AppHome と同 pattern、旧 MainPage L182-204 踏襲)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [verificationOpen, setVerificationOpen] = useState(false);
  const [recoverOpen, setRecoverOpen] = useState(false);
  const [deviceTransferOpen, setDeviceTransferOpen] = useState(false);
  const [gisSettingsOpen, setGisSettingsOpen] = useState(false);

  const pmtilesState = useDistrictPmtiles(districtId);
  const pmtilesMeta = pmtilesState.status === 'ready' ? pmtilesState.meta : null;
  // Sprint BG-DataSettings 6+7 (GAP-A4 / U-H8 / U-H9 / L17): 地区 ColorRule (kind 形式)
  const colorRule = useColorRule();

  // docs/22 GIS overlay (要件4): gisEnabled の地区で GIS レイヤーを重畳 + feature 属性パネル。
  const gis = useDistrictGisLayers(districtId, district?.gisEnabled === true);
  const [gisFeature, setGisFeature] = useState<GisFeatureClickInfo | null>(null);

  const isOnline = useOnlineStatus();
  const { state: outboxState } = useOutbox();
  // docs/03 §7.5-3 層1: 賛同等の耐久冪等 action outbox を mount (online 時 / 復帰時に自動 flush)。
  useActionOutbox();
  // docs/handoff_2026-06-03 根本対処: 楽観 pending 投稿 (path C)。createPost が送信直前に登録した
  // 投稿を、ネットワーク状態 / Firestore リスナー配信に依存せず即時 pending 表示する。
  const optimisticItems = useOptimisticPosts();
  const allPosts = useMemo(
    () => (postsState.status === 'ready' ? postsState.posts : []),
    [postsState],
  );
  // Firestore が配信済みの doc ID 集合 (filter 適用前)。楽観ピンの dedup / cleanup に使う。
  const deliveredPostIds = useMemo(() => new Set(allPosts.map((p) => p.id)), [allPosts]);
  const { filtered: posts, activeFilterCount } = applyPostFilters(
    allPosts,
    filters.toFilterState(currentSessionId, uid, identity.allUids),
  );
  // 未送信投稿は通常マーカー (circle layer) から除外し、pending マーカーで区別表示する。
  // 「未送信」には 2 経路がある:
  //   経路A: outbox (createPost が navigator.onLine===false を検出 → IndexedDB に保留)。
  //   経路B: Firestore オフラインキャッシュ (navigator.onLine===true のまま setDoc → ローカルキュー)。
  //          こちらは posts に現れるが pendingWrite=true (= サーバ未同期)。
  // Android 機内モードでは navigator.onLine が true のまま残り経路B になりがちで、以前は通常ピンに
  // なっていた。両経路を pending マーカーへ統合し、端末差なく未送信を区別表示する (REQ-4.1.15)。
  const syncedPosts = useMemo(() => posts.filter((p) => !p.pendingWrite), [posts]);
  const pendingPosts = useMemo(() => {
    const fromOutbox = outboxState.items
      .filter((it) => it.districtId === districtId && it.location)
      .map((it) => ({
        id: it.clientId ?? `outbox:${it.id ?? it.createdLocalAt}`,
        title: it.title,
        location: it.location,
        // 未送信でもローカル生成済みサムネ (photoThumbBlob) を pending ポップアップに表示する。
        thumbBlob: it.photoThumbBlob ?? null,
      }));
    const fromFirestore = posts.flatMap((p) =>
      p.pendingWrite && p.location
        ? [{ id: `pending:${p.id}`, title: p.title, location: p.location, thumbBlob: null }]
        : [],
    );
    //   経路C: 楽観 pending (createPost が送信直前に登録)。Firestore がまだ doc を配信していない
    //          ものだけ表示する。配信されたら deliveredPostIds で除外 → 同じ marker id
    //          (`pending:<docId>`) で経路B / 通常ピンへ seamless に引き継がれる (二重ピン防止)。
    const fromOptimistic = optimisticItems
      .filter((it) => it.districtId === districtId && it.location && !deliveredPostIds.has(it.id))
      .map((it) => ({
        id: `pending:${it.id}`,
        title: it.title,
        location: it.location,
        thumbBlob: it.thumbBlob,
      }));
    return [...fromOutbox, ...fromFirestore, ...fromOptimistic];
  }, [outboxState.items, districtId, posts, optimisticItems, deliveredPostIds]);

  // 楽観 pending 投稿の後始末: Firestore が同 doc を配信したらストアから除去 (memory hygiene)。
  // 表示上の dedup は上の deliveredPostIds filter で済むが、ストアに Blob を残し続けないよう掃除する。
  useEffect(() => {
    for (const it of optimisticItems) {
      if (deliveredPostIds.has(it.id)) removeOptimisticPost(it.id);
    }
  }, [optimisticItems, deliveredPostIds]);

  // old2 入口フロー移植 (docs/105 §9): 初回参加判定は EntryPage (Firestore membership) に一本化したため、
  // 地図 page の localStorage onboarding gate は撤去。MapPage は onboarding 済前提で render。

  // 地区フォールバック根絶: 地区が未解決 (loading/error) の間は phase2-default を使わず、
  // 解決待ちプレースホルダを全画面表示する。以降 district は非 null に narrowing される。
  if (!district) {
    return <DistrictPendingScreen state={districtState} />;
  }

  return (
    <div className="flex flex-col h-dvh bg-white">
      <header className="bg-white border-b border-slate-200 px-3 py-2 flex items-center gap-2 shrink-0 z-20">
        <DrawerToggleButton onClick={() => setDrawerOpen(true)} />
        <div className="flex-1 min-w-0 flex items-center gap-1.5">
          {/* 地区解決済 (gate 通過後) のみ render されるため、地区名を即時表示。 */}
          <h1 className="font-medium text-sm truncate text-slate-900">{district.name}</h1>
          {/* 地区「強い停止」Phase 1: アカウント (master/担当地区管理者) ログイン中のみ表示。匿名は非表示。 */}
          <AccountRoleBadge />
        </div>
        {/* 案1: 複数セッション参加時のみ、メインセッション選択を「自分のみ」の左に表示 (AppHome と同一)。 */}
        {mainSession.needsMainSelection && (
          <select
            value={currentSessionId ?? ''}
            onChange={(e) => setCurrentSessionId(e.target.value || null)}
            aria-label="メインセッションを選択"
            title="投稿に使うメインセッション"
            className="max-w-[40vw] min-h-touch text-xs px-1.5 py-1 border border-slate-300 rounded bg-white text-slate-700 truncate"
          >
            {mainSession.joinedActiveSessions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        )}
        {/* Phase F (old2 MainPage L144-145 逐語): 自分のみ toggle + 🔍 フィルタ button (badge)。 */}
        <OnlyMineToggle checked={filters.onlyMine} onChange={filters.setOnlyMine} />
        <FilterButton activeFilterCount={activeFilterCount} onClick={() => setFilterOpen(true)} />
        {!isOnline && (
          <span
            className="text-[10px] bg-warning-500 text-white px-1.5 py-0.5 rounded whitespace-nowrap"
            title="オフライン中"
          >
            オフライン
          </span>
        )}
        {outboxState.count > 0 && (
          <span
            className="text-[10px] bg-warning-500 text-white px-1.5 py-0.5 rounded whitespace-nowrap"
            title="未送信の投稿があります"
          >
            ⏳ 未送信 {outboxState.count}
          </span>
        )}
      </header>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        isAuthenticated={auth.status === 'authenticated'}
        onShowAnnouncements={() => setHistoryOpen(true)}
        onShowSurveys={() => setSurveyOpen(true)}
        onShowVerification={() => setVerificationOpen(true)}
        onShowRecover={() => setRecoverOpen(true)}
        onShowSettings={() => setSettingsOpen(true)}
        onShowDeviceTransfer={() => setDeviceTransferOpen(true)}
        onShowGisSettings={() => setGisSettingsOpen(true)}
        onShowPhotoDownload={() => setPhotoDownloadOpen(true)}
      />

      {/* メインコンテンツ — 旧 MapTab L435-471 と同じ absolute inset-0 で MapView が埋める。
          AnnouncementBanner / AnnouncementModal は旧 AnnouncementHost (overlay) 相当として上に重ねる。 */}
      <main className="flex-1 overflow-hidden relative pb-16">
        <MultiTabBanner />
        <AnnouncementBanner districtId={district.id} currentSessionId={currentSessionId} />
        <AnnouncementModal districtId={district.id} currentSessionId={currentSessionId} />

        <div className="absolute inset-0 pb-16">
          <MapView
            posts={syncedPosts}
            pendingPosts={pendingPosts}
            onPinClick={(post) => setDetailPost(post)}
            districtId={district.id}
            pmtilesMeta={pmtilesMeta}
            mode={district.mode}
            colorRule={colorRule}
            markerSize={district.mapDisplay?.markerSize}
            iconSize={district.mapDisplay?.iconSize}
            // Sprint AG/AI: district の `postAreaCenter` を初期 center、`postAreaRadiusM` を
            // zoomForRadius で zoom に変換 (旧 MapTab L156-161 同等)。
            // 両方未設定なら MapView の DEFAULT_CENTER/ZOOM (東京 zoom 13) fallback。
            centerOverride={
              district.postAreaCenter
                ? {
                    lat: district.postAreaCenter.latitude,
                    lng: district.postAreaCenter.longitude,
                  }
                : undefined
            }
            zoomOverride={
              district.postAreaCenter && district.postAreaRadiusM
                ? zoomForRadius(district.postAreaRadiusM, district.postAreaCenter.latitude)
                : undefined
            }
            // Sprint AK: 初回 GPS fix で「地区内なら user 位置に easeTo」のため radius を渡す
            postAreaCenter={district.postAreaCenter}
            postAreaRadiusM={district.postAreaRadiusM}
            // 旧 MapTab L307-412: 右クリック / 長押しで地図上のその地点に pending マーカーを立て、
            // 「ここで投稿」押下で /post へ locationOverride 付き遷移する (マーカー描画は MapView 内)。
            onPostHere={(loc) => {
              const basePath = district.urlToken ? `/m/${district.urlToken}` : '';
              navigate(`${basePath}/post`, {
                state: {
                  locationOverride: { latitude: loc.lat, longitude: loc.lng },
                },
              });
            }}
            // Phase F (old2 MapTab 逐語): ベースマップ初期値 = district.defaultMapStyle。
            // クラスタ(集約)表示は地区設定 mapDisplay.clusterEnabled に連動 (旧アプリの設定を復活、既定 OFF)。
            baseMapStyle={district.defaultMapStyle}
            clusterEnabled={district.mapDisplay?.clusterEnabled === true}
            gisOverlays={gis.overlays}
            onGisFeatureClick={setGisFeature}
            // docs/24 手書きメモ: 地区の mapDisplay.memoEnabled が true のときだけ手書き機能を出す。
            // クラウド共有 (モデルA) 用に作成者 uid / ニックネーム / 現在セッションを渡す。
            memoEnabled={district.mapDisplay?.memoEnabled === true}
            memoUid={uid}
            memoNickname={memoNickname}
            memoSessionId={currentSessionId}
          />
        </div>

        {/* docs/22 GIS 属性パネル (要件4): PC=右サイドパネル / モバイル=ボトムシート。 */}
        <GisFeaturePanel info={gisFeature} onClose={() => setGisFeature(null)} />

        {/* 「ここで投稿」pending マーカー (📍 ボタン + ティアドロップ型ピン) は MapView 内の
            maplibregl.Marker でその地点に描画する (旧 MapTab L381-412)。MapPage 側の overlay は廃止。 */}

        {/* 投稿読込中 indicator (旧 MapTab L439-444 踏襲、Sprint AM で Spinner 共通化 + 30s timeout 後 retry banner) */}
        {postsState.status === 'loading' && !postsLoadTimedOut && (
          <div className="absolute top-2 right-2 bg-white/90 rounded-md shadow px-3 py-1.5 z-10 pointer-events-none">
            <Spinner label="投稿を読み込み中..." size={16} />
          </div>
        )}
        {postsState.status === 'loading' && postsLoadTimedOut && (
          <div className="absolute top-2 right-2 bg-white/95 rounded-md shadow px-3 py-2 z-10 max-w-[80%] flex items-center gap-2">
            <span className="text-xs text-warning-700">読込が遅れています</span>
            <Button variant="secondary" size="sm" onClick={() => setPostsRetryKey((k) => k + 1)}>
              再試行
            </Button>
          </div>
        )}
        {postsState.status === 'error' && (
          <div className="absolute top-2 right-2 bg-white/95 rounded-md shadow px-3 py-2 z-10 max-w-[80%] flex items-center gap-2">
            <span className="text-xs text-danger-600 truncate">
              読込エラー: {postsState.error.message}
            </span>
            <Button variant="secondary" size="sm" onClick={() => setPostsRetryKey((k) => k + 1)}>
              再試行
            </Button>
          </div>
        )}

        {/* Sprint AR (R-33) + Sprint BG-DataSettings 6+7 (U-H8): 凡例 floating button + modal overlay。
            colorRule あれば buckets/cells を凡例エントリ化、なければ mode-default 1 件 fallback。 */}
        <MapLegend mode={district.mode} colorRule={colorRule} />

        {/* Phase F: 全画面 FilterPanel (old2 同一 section)。header の 🔍 button で開く。 */}
        <FilterPanel
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          activeFilterCount={activeFilterCount}
          onReset={filters.reset}
          keyword={filters.keyword}
          onKeywordChange={filters.setKeyword}
          sessions={sessionOptions}
          sessionIds={filters.sessionIds}
          onSessionIdsChange={filters.setSessionIds}
          dateFrom={filters.dateFrom}
          onDateFromChange={filters.setDateFrom}
          dateTo={filters.dateTo}
          onDateToChange={filters.setDateTo}
          hasPhoto={filters.hasPhoto}
          onHasPhotoChange={filters.setHasPhoto}
          minAgreeCount={filters.minAgreeCount}
          onMinAgreeCountChange={filters.setMinAgreeCount}
          postFieldFilters={filters.postFieldFilters}
          onPostFieldFiltersChange={filters.setPostFieldFilters}
          userFieldFilters={filters.userFieldFilters}
          onUserFieldFiltersChange={filters.setUserFieldFilters}
          selectedCategoryCodes={filters.selectedCategoryCodes}
          onSelectedCategoryCodesChange={filters.setSelectedCategoryCodes}
          postFields={district.dataSettings?.postFields ?? []}
          userFields={district.dataSettings?.userFields ?? []}
          colorRule={colorRule}
          categoryMasters={categoryMasters}
        />
      </main>

      <AnnouncementHistoryModal
        districtId={district.id}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
      <SurveyListModal open={surveyOpen} onClose={() => setSurveyOpen(false)} />
      {/* docs/22 GIS 設定 (要件5): レイヤー表示/順序/スタイルのユーザー上書き (gisEnabled 地区のみ Drawer に出る)。 */}
      <GisSettingsModal
        open={gisSettingsOpen}
        onClose={() => setGisSettingsOpen(false)}
        districtId={district.id}
        enabled={district.gisEnabled === true}
        districtName={district.name}
      />
      {auth.status === 'authenticated' && (
        <VerificationListPage
          open={verificationOpen}
          onClose={() => setVerificationOpen(false)}
          districtId={district.id}
          uid={auth.user.uid}
          currentSessionId={currentSessionId}
        />
      )}
      {auth.status === 'authenticated' && (
        <>
          <RecoveryRecoverModal
            open={recoverOpen}
            onClose={() => setRecoverOpen(false)}
            districtName={district.name}
            districtToken={district.urlToken}
          />
          <SettingsModal
            open={settingsOpen}
            onClose={() => setSettingsOpen(false)}
            districtName={district.name}
            uid={auth.user.uid}
            districtToken={district.urlToken}
          />
          <DeviceTransferModal
            open={deviceTransferOpen}
            onClose={() => setDeviceTransferOpen(false)}
          />
        </>
      )}

      <PostDetailModal
        open={detailPost !== null}
        onClose={() => setDetailPost(null)}
        post={detailPost}
        districtId={district.id}
        currentUid={uid}
        selfUids={identity.allUids}
        onDeleted={() => setDetailPost(null)}
        analysisDisplayConfigId={district.analysisDisplayConfigId}
      />

      <PhotoDownloadModal
        open={photoDownloadOpen}
        onClose={() => setPhotoDownloadOpen(false)}
        districtId={district.id}
        uid={uid}
        posts={allPosts}
      />

      <BottomTabBar urlToken={district.urlToken} />
    </div>
  );
}
